import { useEffect, useCallback, useReducer, type ReactElement } from 'react';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { sendExperienceAnalytics } from '@atlassian/jira-issue-analytics/src/services/send-experience-analytics/index.tsx';
import type { ActivitySortOrderType } from '@atlassian/jira-issue-shared-types/src/common/types/activity-sort-order-type.tsx';
import {
	useProjectKey,
	useProjectType,
} from '@atlassian/jira-project-context-service/src/main.tsx';

import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { HistoryItem } from '../../types.tsx';
import { experienceDescription } from './get-experience-tracking-details.tsx';
import { getIssueHistory } from './history-item-generator/index.tsx';
import type { IssueHistoryResult, ServiceResult } from './types.tsx';
import { groupHistoryItems } from './utils.tsx';

const NUM_INITIAL_ITEMS_TO_LOAD_FOR_POST_LOAD_ACTIONS = 100;
const NUM_PAGED_ITEMS_TO_LOAD = 100;

type Status = 'IDLE' | 'ERROR' | 'LOADING_FIRST_PAGE' | 'LOADING_SUBSEQUENT_PAGE';
export type Action =
	| {
			type: 'CHANGE_ISSUE_REQUESTED';
			issueKey: IssueKey;
	  }
	| {
			type: 'FETCH_PAGE_SUCCESS';
			response: IssueHistoryResult;
			issueKey: IssueKey;
	  }
	| {
			type: 'FETCH_PAGE_FAILURE';
			issueKey: IssueKey;
	  }
	| {
			type: 'NEXT_PAGE_REQUESTED';
	  };

export type State = {
	status: Status;
	issueKey: IssueKey;
	historyItems: HistoryItem[];
	nextPageStartIndex: null | number;
};

export const getInitialState = (issueKey: IssueKey): State => ({
	status: 'IDLE',
	issueKey,
	historyItems: [],
	nextPageStartIndex: 0,
});

export const reducer = (state: State, action: Action): State => {
	switch (action.type) {
		case 'FETCH_PAGE_SUCCESS': {
			// Result is stale if issue key changed in the meantime.
			if (action.issueKey !== state.issueKey) {
				return state;
			}

			return {
				...state,
				status: 'IDLE',
				nextPageStartIndex: action.response.isLastPage ? null : action.response.nextPageStartIndex,
				historyItems: [...state.historyItems, ...action.response.history],
			};
		}
		case 'FETCH_PAGE_FAILURE': {
			// Result is stale if issue key changed in the meantime.
			if (action.issueKey !== state.issueKey) {
				return state;
			}
			return { ...state, status: 'ERROR' };
		}
		case 'NEXT_PAGE_REQUESTED': {
			return { ...state, status: 'LOADING_SUBSEQUENT_PAGE' };
		}
		case 'CHANGE_ISSUE_REQUESTED': {
			return {
				issueKey: action.issueKey,
				status: 'LOADING_FIRST_PAGE',
				historyItems: [],
				nextPageStartIndex: 0,
			};
		}
		default:
			// prettier-ignore
			// @ts-expect-error - TS2339 - Property 'type' does not exist on type 'never'.
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(action.type as never);
			// @ts-expect-error - TS2339 - Property 'type' does not exist on type 'never'.
			throw new Error(`Unknown action type in issue history reducer: ${action.type}`);
	}
};

export const useIssueHistory = (
	issueKey: IssueKey,
	sortOrder: ActivitySortOrderType,
): ServiceResult => {
	const [state, dispatch] = useReducer(reducer, getInitialState(issueKey));
	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);

	const fetchPage = useCallback(
		async (pageStartIndex: number, pageSize: number) => {
			try {
				const response = await getIssueHistory(issueKey, pageStartIndex, sortOrder, pageSize);
				sendExperienceAnalytics({
					getExperienceDescription: ({ wasSuccessful }) =>
						experienceDescription({
							experience: 'issueHistory',
							action: 'FETCH_PAGE',
							analyticsSource: 'issueHistoryFetch',
							wasSuccessful,
							projectType,
						}),
				});
				dispatch({ type: 'FETCH_PAGE_SUCCESS', response, issueKey });
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				log.safeErrorWithoutCustomerData(
					'issue.activity.history.fetch-history-page',
					'Query returned errors',
					error,
				);
				sendExperienceAnalytics({
					getExperienceDescription: ({ wasSuccessful, statusCode }) =>
						experienceDescription({
							experience: 'issueHistory',
							action: 'FETCH_PAGE',
							analyticsSource: 'issueHistoryFetch',
							wasSuccessful,
							projectType,
							statusCode,
							errorMessage: error?.message || 'UNKNOWN_ERROR',
							traceId: error?.traceId || '',
						}),
					error,
				});
				dispatch({ type: 'FETCH_PAGE_FAILURE', issueKey });
			}
		},
		[issueKey, sortOrder, projectType],
	);

	useEffect(() => {
		dispatch({ type: 'CHANGE_ISSUE_REQUESTED', issueKey });
		fetchPage(0, NUM_INITIAL_ITEMS_TO_LOAD_FOR_POST_LOAD_ACTIONS);
	}, [fetchPage, issueKey]);

	const loadMore = () => {
		const { status, nextPageStartIndex } = state;
		if (status === 'IDLE' && nextPageStartIndex !== null) {
			dispatch({ type: 'NEXT_PAGE_REQUESTED' });
			fetchPage(nextPageStartIndex, NUM_PAGED_ITEMS_TO_LOAD);
		}
	};

	const { status, historyItems, nextPageStartIndex } = state;

	return {
		loading: status === 'LOADING_FIRST_PAGE',
		loadingMore: status === 'LOADING_SUBSEQUENT_PAGE',
		error: status === 'ERROR',
		hasMore: nextPageStartIndex !== null,
		data: groupHistoryItems(historyItems),
		loadMore,
	};
};

type Props = {
	issueKey: IssueKey;
	sortOrder: ActivitySortOrderType;
	children: (arg1: ServiceResult) => ReactElement;
};

const IssueHistoryService = ({ issueKey, sortOrder, children }: Props) =>
	children(useIssueHistory(issueKey, sortOrder));

export default IssueHistoryService;
